import { useState } from "react";
import { motion } from "framer-motion";
import NetworkIcon from "@core/network-icon";
import { AITimelineContent } from "../../types/ai-templates";

interface TimelineTemplate5Props {
  content: AITimelineContent;
  onUpdate: (newContent: AITimelineContent) => void;
}

export function TimelineTemplate5({
  content,
  onUpdate,
}: TimelineTemplate5Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");
  console.log("content", content);
  // Handle title update
  const handleTitleUpdate = (newText: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    newContent.title = newText;
    onUpdate(newContent);
  };

  // Handle event update
  const handleEventUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  // Check if a specific event is being edited
  const isEditingEvent = (index: number, field: "title" | "description") => {
    return editingIndex === index && editingField === field;
  };

  // Ensure events array exists and has enough elements
  const events =
    Array.isArray(content.steps) && content.steps.length >= 5
      ? content.steps
      : [];
  console.log("events", events);
  // If events are not available, render a fallback
  if (!events.length) {
    return (
      <div className="flex flex-col items-center p-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-4 text-center"
        >
          <h2 className="text-2xl font-bold">
            {content.title || "No Title Provided"}
          </h2>
        </motion.div>
        <p className="text-gray-600 dark:text-gray-400">No events provided.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center p-4 relative">
      {/* Main Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-4 text-center"
      >
        {editingIndex === -1 && editingField === "title" ? (
          <>
            <span className="-translate-y-2/3 top-1/2 left-1/2 text-white bg-blue-600 rounded-full w-6 h-6 flex items-center justify-center absolute -mt-9 ml-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                ></path>
              </svg>
            </span>
            <input
              value={tempValue}
              autoFocus
              onChange={(e) => setTempValue(e.target.value)}
              onBlur={() => {
                setEditingIndex(null);
                setEditingField(null);
                if (tempValue !== content.title) {
                  handleTitleUpdate(tempValue);
                }
              }}
              className="text-2xl font-bold w-full text-center dark:text-gray-300 dark:bg-gray-800"
            />
          </>
        ) : (
          <h2
            className="text-2xl font-bold cursor-pointer"
            onClick={() => {
              setEditingIndex(-1);
              setEditingField("title");
              setTempValue(content.title || "");
            }}
          >
            {content.title || "Untitled"}
          </h2>
        )}
      </motion.div>

      {/* SVG Graphic with Events */}
      <svg
        width="100%"
        viewBox="0 0 888 684"
        preserveAspectRatio="xMidYMid meet"
        style={{
          fill: "none",
          stroke: "none",
          fillRule: "evenodd",
          clipRule: "evenodd",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeMiterlimit: 1.5,
        }}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        {/* Font Import */}
        <style>
          {`@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');`}
        </style>

        {/* Patterns */}
        <defs>
          <pattern
            id="crawlingPattern-5a87971a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#5a87971a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <symbol id="crawlingSymbol">
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                from="0 0"
                to="-12 0"
                dur="0.4s"
                repeatCount="indefinite"
              ></animateTransform>
              <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z"></path>
            </g>
          </symbol>
          <pattern
            id="crawlingPattern-818f641a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#818f641a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-a867651a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#a867651a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-9b7b5f1a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#9b7b5f1a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-958e541a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#958e541a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-0000001b"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#0000001b"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
        </defs>

        {/* Shapes */}
        <g transform="translate(285.3134765625, 170)">
          <g stroke="none" fill="#344e5740">
            <path d="M 102.5304 10L 31.9124 10L 10 77.4855L 25.3127 88.6108L 44.9929 28L 108.379 28L 102.5304 10Z"></path>
          </g>
          <g stroke="none">
            <path d="M 102.5304 10L 31.9124 10L 10 77.4855L 25.3127 88.6108L 44.9929 28L 108.379 28L 102.5304 10Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#1ac3fb"
            strokeWidth="2"
          >
            <path d="M 102.5304 10L 31.9124 10L 10 77.4855L 25.3127 88.6108L 44.9929 28L 108.379 28L 102.5304 10Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#00ade7d7"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 102.5304 10L 31.9124 10L 10 77.4855L 25.3127 88.6108L 44.9929 28L 108.379 28L 102.5304 10Z"></path>
          </g>
        </g>
        <g transform="translate(315.830078125, 205.9662322998047)">
          <g stroke="none" fill="#344e5740">
            <path d="M 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10Z"></path>
          </g>
          <g stroke="none">
            <path d="M 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#1ac3fb"
            strokeWidth="2"
          >
            <path d="M 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#00ade7d7"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10Z"></path>
          </g>
        </g>
        <g transform="translate(240, 310.0908508300781)">
          <g stroke="none" fill="#4b533a40">
            <path d="M 82.6751 107.5342L 31.1567 70.0783L 50.664 10L 31.7389 10L 10 76.9511L 67.365 118.658L 82.6751 107.5342Z"></path>
          </g>
          <g stroke="none">
            <path d="M 82.6751 107.5342L 31.1567 70.0783L 50.664 10L 31.7389 10L 10 76.9511L 67.365 118.658L 82.6751 107.5342Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#a6da37"
            strokeWidth="2"
          >
            <path d="M 82.6751 107.5342L 31.1567 70.0783L 50.664 10L 31.7389 10L 10 76.9511L 67.365 118.6576L 82.6751 107.5342Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#6b9d0056"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 82.6751 107.5342L 31.1567 70.0783L 50.664 10L 31.7389 10L 10 76.9511L 67.365 118.6576L 82.6751 107.5342Z"></path>
          </g>
        </g>
        <g transform="translate(282.00390625, 310.07122802734375)">
          <g stroke="none" fill="#4b533a40">
            <path d="M 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10L 10 63.5155Z"></path>
          </g>
          <g stroke="none">
            <path d="M 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10L 10 63.5155Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#a6da37"
            strokeWidth="2"
          >
            <path d="M 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10L 10 63.5155Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#6b9d0056"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10.000001L 27.3882 10L 10 63.5155Z"></path>
          </g>
        </g>
        <g transform="translate(358.9482421875, 445.4239807128906)">
          <g stroke="none" fill="#68403f40">
            <path d="M 124.235 28.0019L 67.0514 69.5765L 10 28.0979L 15.8492 10.096L 67.0514 47.322L 118.386 10L 124.235 28.0019Z"></path>
          </g>
          <g stroke="none">
            <path d="M 124.235 28.0019L 67.0514 69.5765L 10 28.0979L 15.8492 10.096L 67.0514 47.322L 118.386 10L 124.235 28.0019Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#fb6762"
            strokeWidth="2"
          >
            <path d="M 124.2349 28.0019L 67.0514 69.5765L 10 28.0979L 15.8492 10.096L 67.0514 47.322L 118.3857 10L 124.2349 28.0019Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#f9080063"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 124.2349 28.0019L 67.0514 69.5765L 10 28.0979L 15.8492 10.096L 67.0514 47.322L 118.3857 10L 124.2349 28.0019Z"></path>
          </g>
        </g>
        <g transform="translate(370.560546875, 374.41107177734375)">
          <g stroke="none" fill="#68403f40">
            <path d="M 55.523 96.5899L 101.046 63.5155L 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899Z"></path>
          </g>
          <g stroke="none">
            <path d="M 55.523 96.5899L 101.046 63.5155L 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#fb6762"
            strokeWidth="2"
          >
            <path d="M 55.523 96.5899L 101.046 63.5155L 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#f9080063"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 55.523 96.5899L 101.046 63.5155L 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899Z"></path>
          </g>
        </g>
        <g transform="translate(519.4052734375, 310.09088134765625)">
          <g stroke="none" fill="#5c493840">
            <path d="M 25.3101 118.599L 82.5946 76.9511L 60.8557 10L 41.9306 10L 61.4379 70.0783L 10 107.4756L 25.3101 118.599Z"></path>
          </g>
          <g stroke="none">
            <path d="M 25.3101 118.599L 82.5946 76.9511L 60.8557 10L 41.9306 10L 61.4379 70.0783L 10 107.4756L 25.3101 118.599Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#f79438"
            strokeWidth="2"
          >
            <path d="M 25.3101 118.599L 82.5946 76.9511L 60.8557 10L 41.9306 10L 61.4379 70.0783L 10 107.4756L 25.3101 118.599Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#f57600ab"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 25.3101 118.599L 82.5946 76.9511L 60.8557 10L 41.9306 10L 61.4379 70.0783L 10 107.4756L 25.3101 118.599Z"></path>
          </g>
        </g>
        <g transform="translate(459.1162109375, 310.07122802734375)">
          <g stroke="none" fill="#5c493840">
            <path d="M 101.046 63.5155L 83.6578 10.000008L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155Z"></path>
          </g>
          <g stroke="none">
            <path d="M 101.046 63.5155L 83.6578 10.000008L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#f79438"
            strokeWidth="2"
          >
            <path d="M 101.046 63.5155L 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#f57600ab"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 101.046 63.5155L 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155Z"></path>
          </g>
        </g>
        <g transform="translate(448.4716796875, 170.000732421875)">
          <g stroke="none" fill="#54502f40">
            <path d="M 10 28L 73.2222 28L 92.9341 88.7083L 108.2468 77.5829L 86.3027 10L 15.8486 10L 10 28Z"></path>
          </g>
          <g stroke="none">
            <path d="M 10 28L 73.2222 28L 92.9341 88.7083L 108.2468 77.5829L 86.3027 10L 15.8486 10L 10 28Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#ffe711"
            strokeWidth="2"
          >
            <path d="M 10 28L 73.2222 28L 92.9341 88.7083L 108.2468 77.5829L 86.3027 10L 15.8486 10L 10 28Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#e9d100ff"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 10 28L 73.2222 28L 92.9341 88.7083L 108.2468 77.5829L 86.3027 10L 15.8486 10L 10 28Z"></path>
          </g>
        </g>
        <g transform="translate(425.2919921875, 205.96678161621094)">
          <g stroke="none" fill="#54502f40">
            <path d="M 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10Z"></path>
          </g>
          <g stroke="none">
            <path d="M 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#ffe711"
            strokeWidth="2"
          >
            <path d="M 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10Z"></path>
          </g>
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke="#e9d100ff"
            strokeWidth="2"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            ></animate>
            <path d="M 83.6578 10L 27.3882 10L 10 63.5155L 55.523 96.5899L 101.046 63.5155L 83.6578 10Z"></path>
          </g>
        </g>

        {/* Editable Event Descriptions */}
        <g transform="translate(44, 206)">
          <g stroke="none" fill="#171717">
            {isEditingEvent(0, "description") ? (
              <foreignObject x="12" y="10" width="150" height="60">
                <textarea
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[0].description) {
                      handleEventUpdate(0, "description", tempValue);
                    }
                  }}
                  className="text-sm w-full text-left dark:text-gray-400 dark:bg-gray-800"
                  style={{ font: "15px Roboto, sans-serif", height: "60px" }}
                  onFocus={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
              </foreignObject>
            ) : (
              <text
                style={{ font: "15px Roboto, sans-serif", whiteSpace: "pre" }}
                fontSize="15px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(0);
                  setEditingField("description");
                  setTempValue(events[0].description || "");
                }}
              >
                <tspan x="12" y="27.5" dominant-baseline="ideographic">
                  {events[0].description
                    ? events[0].description.split(" ").slice(0, 3).join(" ")
                    : ""}
                </tspan>
                <tspan x="12" y="45.5" dominant-baseline="ideographic">
                  {events[0].description
                    ? events[0].description.split(" ").slice(3).join(" ")
                    : ""}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(64, 410)">
          <g stroke="none" fill="#171717">
            {isEditingEvent(1, "description") ? (
              <foreignObject x="12" y="10" width="150" height="80">
                <textarea
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[1].description) {
                      handleEventUpdate(1, "description", tempValue);
                    }
                  }}
                  className="text-sm w-full text-left dark:text-gray-400 dark:bg-gray-800"
                  style={{ font: "15px Roboto, sans-serif", height: "80px" }}
                  onFocus={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
              </foreignObject>
            ) : (
              <text
                style={{ font: "15px Roboto, sans-serif", whiteSpace: "pre" }}
                fontSize="15px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(1);
                  setEditingField("description");
                  setTempValue(events[1].description || "");
                }}
              >
                <tspan x="12" y="30.5" dominant-baseline="ideographic">
                  {events[1].description
                    ? events[1].description.split(" ").slice(0, 3).join(" ")
                    : ""}
                </tspan>
                <tspan x="12" y="48.5" dominant-baseline="ideographic">
                  {events[1].description
                    ? events[1].description.split(" ").slice(3, 6).join(" ")
                    : ""}
                </tspan>
                <tspan x="12" y="66.5" dominant-baseline="ideographic">
                  {events[1].description
                    ? events[1].description.split(" ").slice(6).join(" ")
                    : ""}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(328, 550)">
          <g stroke="none" fill="#171717">
            {isEditingEvent(2, "description") ? (
              <foreignObject x="12" y="10" width="150" height="60">
                <textarea
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[2].description) {
                      handleEventUpdate(2, "description", tempValue);
                    }
                  }}
                  className="text-sm w-full text-left dark:text-gray-400 dark:bg-gray-800"
                  style={{ font: "15px Roboto, sans-serif", height: "60px" }}
                  onFocus={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
              </foreignObject>
            ) : (
              <text
                style={{ font: "15px Roboto, sans-serif", whiteSpace: "pre" }}
                fontSize="15px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(2);
                  setEditingField("description");
                  setTempValue(events[2].description || "");
                }}
              >
                <tspan x="12" y="27.5" dominant-baseline="ideographic">
                  {events[2].description
                    ? events[2].description.split(" ").slice(0, 3).join(" ")
                    : ""}
                </tspan>
                <tspan x="12" y="45.5" dominant-baseline="ideographic">
                  {events[2].description
                    ? events[2].description.split(" ").slice(3).join(" ")
                    : ""}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(620, 374)">
          <g stroke="none" fill="#171717">
            {isEditingEvent(3, "description") ? (
              <foreignObject x="12" y="10" width="150" height="60">
                <textarea
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[3].description) {
                      handleEventUpdate(3, "description", tempValue);
                    }
                  }}
                  className="text-sm w-full text-left dark:text-gray-400 dark:bg-gray-800"
                  style={{ font: "15px Roboto, sans-serif", height: "60px" }}
                  onFocus={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
              </foreignObject>
            ) : (
              <text
                style={{ font: "15px Roboto, sans-serif", whiteSpace: "pre" }}
                fontSize="15px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(3);
                  setEditingField("description");
                  setTempValue(events[3].description || "");
                }}
              >
                <tspan x="12" y="27.5" dominant-baseline="ideographic">
                  {events[3].description
                    ? events[3].description.split(" ").slice(0, 4).join(" ")
                    : ""}
                </tspan>
                <tspan x="12" y="45.5" dominant-baseline="ideographic">
                  {events[3].description
                    ? events[3].description.split(" ").slice(4).join(" ")
                    : ""}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(560, 170)">
          <g stroke="none" fill="#171717">
            {isEditingEvent(4, "description") ? (
              <foreignObject x="12" y="10" width="150" height="60">
                <textarea
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[4].description) {
                      handleEventUpdate(4, "description", tempValue);
                    }
                  }}
                  className="text-sm w-full text-left dark:text-gray-400 dark:bg-gray-800"
                  style={{ font: "15px Roboto, sans-serif", height: "60px" }}
                  onFocus={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
              </foreignObject>
            ) : (
              <text
                style={{ font: "15px Roboto, sans-serif", whiteSpace: "pre" }}
                fontSize="15px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(4);
                  setEditingField("description");
                  setTempValue(events[4].description || "");
                }}
              >
                <tspan x="12" y="27.5" dominant-baseline="ideographic">
                  {events[4].description
                    ? events[4].description.split(" ").slice(0, 4).join(" ")
                    : ""}
                </tspan>
                <tspan x="12" y="45.5" dominant-baseline="ideographic">
                  {events[4].description
                    ? events[4].description.split(" ").slice(4).join(" ")
                    : ""}
                </tspan>
              </text>
            )}
          </g>
        </g>

        {/* Editable Event Titles */}
        <g transform="translate(44, 170)">
          <g stroke="none" fill={events[0].color || "#6D4C41"}>
            {isEditingEvent(0, "title") ? (
              <foreignObject x="12" y="10" width="100" height="40">
                <input
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[0].title) {
                      handleEventUpdate(0, "title", tempValue);
                    }
                  }}
                  className="text-lg font-semibold w-full text-left dark:text-gray-300 dark:bg-gray-800"
                  style={{ font: "bold 20px Roboto, sans-serif" }}
                />
              </foreignObject>
            ) : (
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(0);
                  setEditingField("title");
                  setTempValue(events[0].title || "");
                }}
              >
                <tspan x="12" y="33.5" dominant-baseline="ideographic">
                  {events[0].title || "Untitled"}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(64, 374)">
          <g stroke="none" fill={events[1].color || "#795548"}>
            {isEditingEvent(1, "title") ? (
              <foreignObject x="12" y="10" width="100" height="40">
                <input
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[1].title) {
                      handleEventUpdate(1, "title", tempValue);
                    }
                  }}
                  className="text-lg font-semibold w-full text-left dark:text-gray-300 dark:bg-gray-800"
                  style={{ font: "bold 20px Roboto, sans-serif" }}
                />
              </foreignObject>
            ) : (
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(1);
                  setEditingField("title");
                  setTempValue(events[1].title || "");
                }}
              >
                <tspan x="12" y="33.5" dominant-baseline="ideographic">
                  {events[1].title || "Untitled"}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(326, 520)">
          <g stroke="none" fill={events[2].color || "#8D6E63"}>
            {isEditingEvent(2, "title") ? (
              <foreignObject x="12" y="10" width="100" height="40">
                <input
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[2].title) {
                      handleEventUpdate(2, "title", tempValue);
                    }
                  }}
                  className="text-lg font-semibold w-full text-left dark:text-gray-300 dark:bg-gray-800"
                  style={{ font: "bold 20px Roboto, sans-serif" }}
                />
              </foreignObject>
            ) : (
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(2);
                  setEditingField("title");
                  setTempValue(events[2].title || "");
                }}
              >
                <tspan x="12" y="33.5" dominant-baseline="ideographic">
                  {events[2].title || "Untitled"}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(620, 338)">
          <g stroke="none" fill={events[3].color || "#A1887F"}>
            {isEditingEvent(3, "title") ? (
              <foreignObject x="12" y="10" width="100" height="40">
                <input
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[3].title) {
                      handleEventUpdate(3, "title", tempValue);
                    }
                  }}
                  className="text-lg font-semibold w-full text-left dark:text-gray-300 dark:bg-gray-800"
                  style={{ font: "bold 20px Roboto, sans-serif" }}
                />
              </foreignObject>
            ) : (
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(3);
                  setEditingField("title");
                  setTempValue(events[3].title || "");
                }}
              >
                <tspan x="12" y="33.5" dominant-baseline="ideographic">
                  {events[3].title || "Untitled"}
                </tspan>
              </text>
            )}
          </g>
        </g>
        <g transform="translate(560, 134)">
          <g stroke="none" fill={events[4].color || "#BCAAA4"}>
            {isEditingEvent(4, "title") ? (
              <foreignObject x="12" y="10" width="100" height="40">
                <input
                  value={tempValue}
                  autoFocus
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== events[4].title) {
                      handleEventUpdate(4, "title", tempValue);
                    }
                  }}
                  className="text-lg font-semibold w-full text-left dark:text-gray-300 dark:bg-gray-800"
                  style={{ font: "bold 20px Roboto, sans-serif" }}
                />
              </foreignObject>
            ) : (
              <text
                style={{
                  font: "bold 20px Roboto, sans-serif",
                  whiteSpace: "pre",
                }}
                fontWeight="bold"
                fontSize="20px"
                fontFamily="Roboto, sans-serif"
                onClick={() => {
                  setEditingIndex(4);
                  setEditingField("title");
                  setTempValue(events[4].title || "");
                }}
              >
                <tspan x="12" y="33.5" dominant-baseline="ideographic">
                  {events[4].title || "Untitled"}
                </tspan>
              </text>
            )}
          </g>
        </g>

        {/* Icons */}
        <g transform="translate(353, 235)">
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={events[0].color || "#6D4C41"}
            strokeWidth="2"
            transform="scale(1.5)"
          >
            <NetworkIcon
              value={events[0].icon_description || "book knowledge concept"}
              type="LUCIDE"
            />
          </g>
        </g>
        <g transform="translate(463, 235)">
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={events[1].color || "#795548"}
            strokeWidth="2"
            transform="scale(1.5)"
          >
            <NetworkIcon
              value={events[1].icon_description || "person mirror reflect"}
              type="LUCIDE"
            />
          </g>
        </g>
        <g transform="translate(320, 340)">
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={events[2].color || "#8D6E63"}
            strokeWidth="2"
            transform="scale(1.5)"
          >
            <NetworkIcon
              value={events[2].icon_description || "people group community"}
              type="LUCIDE"
            />
          </g>
        </g>
        <g transform="translate(496, 342)">
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={events[3].color || "#A1887F"}
            strokeWidth="2"
            transform="scale(1.5)"
          >
            <NetworkIcon
              value={events[3].icon_description || "computer screen data"}
              type="LUCIDE"
            />
          </g>
        </g>
        <g transform="translate(408, 405)">
          <g
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            stroke={events[4].color || "#BCAAA4"}
            strokeWidth="2"
            transform="scale(1.5)"
          >
            <NetworkIcon
              value={events[4].icon_description || "castle knight shield"}
              type="LUCIDE"
            />
          </g>
        </g>
      </svg>
    </div>
  );
}
